import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { addDriver, updateDriver } from '../utils/api';

/**
 * Modal component for adding or editing a driver
 * @param {Object} props Component props
 * @param {boolean} props.isOpen Whether the modal is open
 * @param {Function} props.onClose Function to call when the modal is closed
 * @param {Object|null} props.driver Driver object to edit (null if adding a new driver)
 * @param {Function} props.onDriverSaved Function to call when a driver is saved
 */
export default function EditDriverModal({ isOpen, onClose, driver, onDriverSaved }) {
  const [driverForm, setDriverForm] = useState({
    name: '',
    home_address: '',
    latitude: null,
    longitude: null
  });
  const [autocomplete, setAutocomplete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isAddingDriver = !driver;

  // Initialize form when driver prop changes
  useEffect(() => {
    if (driver) {
      setDriverForm({
        name: driver.name || '',
        home_address: driver.home_address || '',
        latitude: driver.latitude || null,
        longitude: driver.longitude || null
      });
    } else {
      setDriverForm({
        name: '',
        home_address: '',
        latitude: null,
        longitude: null
      });
    }
  }, [driver, isOpen]);

  // Add style for Google Places Autocomplete
  useEffect(() => {
    // Add the styles for Google Places autocomplete
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .pac-container {
        z-index: 10001 !important;
        margin-top: 2px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
          'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        border-radius: 0.375rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        border: 1px solid #e5e7eb;
      }
      .pac-item {
        padding: 8px 12px;
        font-size: 0.875rem;
        cursor: pointer;
      }
      .pac-item:hover {
        background-color: #f3f4f6;
      }
      .pac-item-query {
        font-size: 0.875rem;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      // Cleanup on unmount
      document.head.removeChild(styleEl);
    };
  }, []);

  // Initialize Google Places Autocomplete when modal opens
  useEffect(() => {
    if (isOpen && window.google && window.google.maps && window.google.maps.places) {
      setTimeout(() => {
        const addressInput = document.getElementById('home_address');
        if (addressInput) {
          const autocompleteInstance = new window.google.maps.places.Autocomplete(addressInput);
          setAutocomplete(autocompleteInstance);

          // Add place_changed event listener
          autocompleteInstance.addListener('place_changed', () => {
            const place = autocompleteInstance.getPlace();
            if (place.geometry) {
              const lat = place.geometry.location.lat();
              const lng = place.geometry.location.lng();
              console.log('Place selected:', place.formatted_address);
              console.log('Latitude:', lat);
              console.log('Longitude:', lng);
              
              setDriverForm(prev => ({
                ...prev,
                home_address: place.formatted_address,
                latitude: lat,
                longitude: lng
              }));
            }
          });
        }
      }, 100); // Small delay to ensure the DOM is ready
    }
  }, [isOpen]);

  const handleDriverFormChange = (e) => {
    const { name, value } = e.target;
    
    // Update all fields in the form
    setDriverForm(prev => ({
      ...prev,
      [name]: value,
      // Only reset lat/long when address changes manually
      ...(name === 'home_address' ? { latitude: null, longitude: null } : {})
    }));
  };

  const handleDriverFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!driverForm.name.trim()) {
      toast.error('Driver name is required');
      return;
    }
    
    if (!driverForm.latitude || !driverForm.longitude) {
      toast.error('Please select an address from the suggestions');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Include latitude and longitude in the data sent to the backend
      const driverData = {
        name: driverForm.name,
        home_address: driverForm.home_address,
        latitude: driverForm.latitude,
        longitude: driverForm.longitude
      };
      
      let savedDriver;
      
      if (isAddingDriver) {
        // Add new driver
        savedDriver = await addDriver(driverData);
        toast.success('Driver added successfully');
      } else {
        // Update existing driver
        savedDriver = await updateDriver(driver.id, driverData);
        toast.success('Driver updated successfully');
      }
      
      // Notify parent component that a driver was saved
      onDriverSaved(savedDriver);
      onClose();
    } catch (error) {
      const action = isAddingDriver ? 'add' : 'update';
      toast.error(`Failed to ${action} driver: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything if the modal is not open
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg overflow-hidden shadow-xl max-w-md w-full" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {isAddingDriver ? 'Add New Driver' : 'Edit Driver'}
          </h3>
        </div>
        
        {isSubmitting ? (
          <div className="flex flex-col items-center justify-center p-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Saving driver information...</p>
          </div>
        ) : (
          <form onSubmit={handleDriverFormSubmit} className="p-6">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={driverForm.name}
                onChange={handleDriverFormChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter driver name"
              />
            </div>
            
            <div className="mb-6" style={{ position: 'relative', zIndex: 10000 }}>
              <label htmlFor="home_address" className="block text-sm font-medium text-gray-700 mb-1">
                Home Address *
              </label>
              <input
                type="text"
                id="home_address"
                name="home_address"
                value={driverForm.home_address}
                onChange={handleDriverFormChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter home address"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {isAddingDriver ? 'Add Driver' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
} 